import {
    AfterViewInit,
    Component,
    effect,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import {
    MatPaginator,
    MatPaginatorModule,
    PageEvent
} from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GrpcStatusEvent } from '@ngx-grpc/common';
import { AuthService } from '../../../authentication/service/auth.service';
import { UsersService } from '../users-service/users.service';
import { SearchInputWithDebouncingComponent } from '../../../ui-components/search-input-with-debouncing/search-input-with-debouncing.component';
import { MatDialog } from '@angular/material/dialog';
import { UpdateUserComponent } from '../update-user/update-user.component';
import { IUser } from '../user-interfaces/user-interfaces';
import { ResetPasswordComponent } from '../reset-password/reset-password.component';
import { BufferProgressBarComponent } from '../../../ui-components/buffer-progress-bar/buffer-progress-bar.component';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-users-list',
    imports: [
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        MatTableModule,
        MatPaginatorModule,
        MatCheckboxModule,
        MatTooltipModule,
        SearchInputWithDebouncingComponent,
        BufferProgressBarComponent,
        MatIcon,
        TranslatePipe
    ],
    templateUrl: './users-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './users-list.component.scss'
})
export class UsersListComponent implements OnInit, AfterViewInit {
    displayedColumns: string[] = [
        'id',
        'ّfullName',
        'email',
        'userName',
        'action'
    ];
    dataSource = new MatTableDataSource<IUser>([]);
    pageSize: number = 8;
    page: number = 1;
    search: string = '';
    isLoading: boolean = false;
    totalUsers: number = 0;
    hasAccessToAdd: boolean = this.authService.hasAccess([
        'Identity.User.Create'
    ]);
    hasAccessToUpdate: boolean = this.authService.hasAccess([
        'Identity.User.Edit'
    ]);

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(
        private usersService: UsersService,
        private authService: AuthService,
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute
    ) {
        effect(() => {
            if (this.usersService.getUserUpdateSignal()()) {
                this.fetchUsers(this.page, this.pageSize, this.search);
                this.usersService.getUserUpdateSignal().set(false);
            }
        });
    }

    ngOnInit(): void {
        this.fetchUsers(this.page, this.pageSize, this.search);
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.page = this.paginator.pageIndex;
        this.pageSize = this.paginator.pageSize;
        // new data call after changeing the paginator settings
        this.paginator.page.subscribe((event: PageEvent) => {
            this.page = event.pageIndex + 1;
            this.pageSize = event.pageSize;
            this.fetchUsers(this.page, this.pageSize, this.search);
        });
    }

    // call users data
    fetchUsers(page: number, pageSize: number, search: string): void {
        this.isLoading = true;
        this.usersService.getUsersList(page, pageSize, search).subscribe({
            next: response => {
                this.dataSource = new MatTableDataSource<any>(response.data);
                this.totalUsers = response.total;
            },
            error: (error: GrpcStatusEvent) => {
                if (
                    error.statusCode === 16 ||
                    error.statusMessage === 'UNAUTHENTICATED'
                ) {
                    this.authService.logOut();
                }
            },
            complete: () => {
                this.isLoading = false;
            }
        });
    }
    // Search Filter
    onSearch(event: any): void {
        this.search = event;
        this.paginator.firstPage();
        this.fetchUsers(this.page, this.pageSize, this.search);
    }

    // open edit user dialog
    openEditUserDialog(userId: number): void {
        const buttonElement = document.activeElement as HTMLElement;
        buttonElement.blur();
        this.dialog.open(UpdateUserComponent, {
            data: userId
        });
    }

    //open reset password dialog
    openResetPasswordDialog(userId: number): void {
        const buttonElement = document.activeElement as HTMLElement;
        buttonElement.blur();
        //open reset password dialog
        this.dialog.open(ResetPasswordComponent, {
            data: userId
        });
    }

    openPlantEditor(row: IUser): void {
        if (!row?.id) {
            return;
        }

        this.router.navigate([
            '/plants/add-plant',
            row.id
        ]);
    }
}
